import { Html, Line } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useDeviceCapabilities } from "../../hooks/useDeviceCapabilities";
import {
	formatZoneDuration,
	formatZoneMultiplier,
	getZoneColorRGB,
} from "../../services/zoneUtils";
import { useObserverStore } from "../../stores/observerStore";
import { type StarSystem, getGalaxyDotSize, getStarColor } from "../../types/celestial";
import { AmbientColorPatches } from "./AmbientColorPatches";
import { ConstellationLines } from "./ConstellationLines";
import { NebulaBackground } from "./NebulaBackground";
import { ParallaxStars } from "./ParallaxStars";
import { getCircleSpriteTexture, getDensityFogTexture, getStarGlowTexture } from "./textures";
import { entryAnimationRef, screenToWorld, useViewportSync } from "./useViewportSync";

// --- Constants ---

const BASE_SCALE = 10;
const DISTANCE_RINGS = [10, 25, 50, 100, 200, 300];
const HIT_RADIUS_MOUSE = 20;
const HIT_RADIUS_TOUCH = 40;
const GLOW_SIZE_MULT = 3;

interface PositionedSystem {
	system: StarSystem;
	px: number;
	py: number;
}

interface DensityCluster {
	x: number;
	y: number;
	radius: number;
	density: number;
}

// --- Density cluster computation ---

function computeDensityClusters(systems: StarSystem[]): DensityCluster[] {
	const gridSize = 50;
	const bins = new Map<string, { sumX: number; sumY: number; count: number }>();

	for (const sys of systems) {
		const gx = Math.floor(sys.x / gridSize);
		const gy = Math.floor(sys.y / gridSize);
		const key = `${gx},${gy}`;
		const bin = bins.get(key) ?? { sumX: 0, sumY: 0, count: 0 };
		bin.sumX += sys.x;
		bin.sumY += sys.y;
		bin.count += 1;
		bins.set(key, bin);
	}

	const clusters: DensityCluster[] = [];
	for (const bin of bins.values()) {
		if (bin.count < 3) continue;
		clusters.push({
			x: (bin.sumX / bin.count) * BASE_SCALE,
			y: (bin.sumY / bin.count) * BASE_SCALE,
			radius: gridSize * BASE_SCALE * 0.8,
			density: Math.min(1, bin.count / 15),
		});
	}
	return clusters;
}

// --- Sub-components ---

function GalaxyStars({
	systems,
	onHover,
}: {
	systems: StarSystem[];
	onHover: (sys: PositionedSystem | null, screenX: number, screenY: number) => void;
}): React.JSX.Element {
	const coreRef = useRef<THREE.Points>(null);
	const glowRef = useRef<THREE.Points>(null);

	const count = systems.length;

	// Pre-compute positions, colors, sizes, twinkle seeds
	const { positions, colors, coreSizes, glowSizes, seeds } = useMemo(() => {
		const pos = new Float32Array(count * 3);
		const col = new Float32Array(count * 3);
		const cSizes = new Float32Array(count);
		const gSizes = new Float32Array(count);
		const sd = new Float32Array(count);
		const tmpColor = new THREE.Color();

		const ZONE_TINT = 0.35; // 35% zone color blending

		for (let i = 0; i < count; i++) {
			const sys = systems[i];
			if (!sys) continue;
			pos[i * 3] = sys.x * BASE_SCALE;
			pos[i * 3 + 1] = -(sys.y * BASE_SCALE); // Y-flip for Three.js
			pos[i * 3 + 2] = 0;

			const colorHex = getStarColor(sys.star.spectralClass);
			tmpColor.setStyle(colorHex);

			// Blend spectral color with zone color
			const [zr, zg, zb] = getZoneColorRGB(sys.zone);
			col[i * 3] = tmpColor.r * (1 - ZONE_TINT) + zr * ZONE_TINT;
			col[i * 3 + 1] = tmpColor.g * (1 - ZONE_TINT) + zg * ZONE_TINT;
			col[i * 3 + 2] = tmpColor.b * (1 - ZONE_TINT) + zb * ZONE_TINT;

			const dotSize = getGalaxyDotSize(sys.star.apparentMagnitude);
			cSizes[i] = dotSize * 2; // screen pixels (Points size is in pixels with sizeAttenuation=false)
			gSizes[i] = dotSize * GLOW_SIZE_MULT * 2;

			// Spatial twinkle seed: nearby stars get similar phases (wave effect),
			// distant stars get very different phases. Small ID jitter adds local variation.
			const posPhase = sys.x * 0.03 + sys.y * 0.07;
			const idJitter = (sys.id.charCodeAt(0) % 17) * 0.25;
			sd[i] = posPhase + idJitter;
		}

		return { positions: pos, colors: col, coreSizes: cSizes, glowSizes: gSizes, seeds: sd };
	}, [systems, count]);

	// Opacity buffer for twinkle animation
	const opacities = useMemo(() => new Float32Array(count).fill(1), [count]);

	const spriteTexture = useMemo(() => getCircleSpriteTexture(), []);

	// Pre-compute per-star distance from origin (for entry animation fade-in)
	const distances = useMemo(() => {
		const dist = new Float32Array(count);
		let maxDist = 0;
		for (let i = 0; i < count; i++) {
			const x = positions[i * 3] ?? 0;
			const y = positions[i * 3 + 1] ?? 0;
			const d = Math.sqrt(x * x + y * y);
			dist[i] = d;
			if (d > maxDist) maxDist = d;
		}
		// Normalize to 0-1
		if (maxDist > 0) {
			for (let i = 0; i < count; i++) {
				dist[i] = (dist[i] ?? 0) / maxDist;
			}
		}
		return dist;
	}, [positions, count]);

	// Twinkle animation + entry fade-in
	useFrame(({ clock }) => {
		const time = clock.getElapsedTime() * 1000;
		const entry = entryAnimationRef.current;

		for (let i = 0; i < count; i++) {
			const seed = seeds[i] ?? 0;
			// Two overlapping sine waves at different frequencies for organic twinkle
			const wave1 = Math.sin(time * 0.0015 + seed);
			const wave2 = Math.sin(time * 0.0009 + seed * 2.3);
			let opacity = 0.6 + 0.25 * wave1 + 0.15 * wave2;

			// During entry: fade stars in based on distance from center
			if (entry.active) {
				const starDist = distances[i] ?? 0;
				// Stars at center appear first, outer stars later
				const fadeThreshold = entry.progress * 1.5; // overshoot so all appear before end
				const starFade = Math.min(1, Math.max(0, (fadeThreshold - starDist) * 3));
				opacity *= starFade;
			}

			opacities[i] = opacity;
		}
		if (coreRef.current) {
			const attr = coreRef.current.geometry.getAttribute("opacity");
			if (attr) (attr as THREE.BufferAttribute).needsUpdate = true;
		}
		if (glowRef.current) {
			const attr = glowRef.current.geometry.getAttribute("opacity");
			if (attr) (attr as THREE.BufferAttribute).needsUpdate = true;
		}
	});

	// Hit testing on pointer move — skip hover on touch (tap handles selection)
	const handlePointerMove = useCallback(
		(e: PointerEvent) => {
			if (e.pointerType === "touch") return;

			const rect = (e.target as HTMLElement).getBoundingClientRect();
			const screenX = e.clientX - rect.left;
			const screenY = e.clientY - rect.top;
			const { worldX, worldY } = screenToWorld(screenX, screenY);

			const hitRadius = e.pointerType === "pen" ? HIT_RADIUS_TOUCH : HIT_RADIUS_MOUSE;
			const threshold = hitRadius / useObserverStore.getState().galaxyView.scale;
			let nearest: PositionedSystem | null = null;
			let minDist = threshold;

			for (const system of systems) {
				const px = system.x * BASE_SCALE;
				const py = system.y * BASE_SCALE;
				const dist = Math.hypot(px - worldX, py - worldY);
				if (dist < minDist) {
					minDist = dist;
					nearest = { system, px, py };
				}
			}

			onHover(nearest, screenX, screenY);
		},
		[systems, onHover],
	);

	useEffect(() => {
		const canvas = document.querySelector("canvas");
		if (!canvas) return;
		canvas.addEventListener("pointermove", handlePointerMove);
		return () => canvas.removeEventListener("pointermove", handlePointerMove);
	}, [handlePointerMove]);

	// Custom ShaderMaterial for per-vertex size + opacity
	const starVertexShader = `
		attribute float size;
		attribute float opacity;
		varying float vOpacity;
		varying vec3 vColor;
		void main() {
			vColor = color;
			vOpacity = opacity;
			vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
			gl_PointSize = size;
			gl_Position = projectionMatrix * mvPosition;
		}
	`;

	const coreFragmentShader = `
		uniform sampler2D map;
		varying float vOpacity;
		varying vec3 vColor;
		void main() {
			vec4 tex = texture2D(map, gl_PointCoord);
			if (tex.a < 0.01) discard;
			gl_FragColor = vec4(vColor * tex.rgb, tex.a * vOpacity);
		}
	`;

	const glowFragmentShader = `
		uniform sampler2D map;
		varying float vOpacity;
		varying vec3 vColor;
		void main() {
			vec4 tex = texture2D(map, gl_PointCoord);
			if (tex.a < 0.01) discard;
			gl_FragColor = vec4(vColor * tex.rgb, tex.a * vOpacity * 0.35);
		}
	`;

	const coreMaterial = useMemo(
		() =>
			new THREE.ShaderMaterial({
				uniforms: { map: { value: spriteTexture } },
				vertexShader: starVertexShader,
				fragmentShader: coreFragmentShader,
				vertexColors: true,
				transparent: true,
				depthWrite: false,
			}),
		[spriteTexture],
	);

	const glowMaterial = useMemo(
		() =>
			new THREE.ShaderMaterial({
				uniforms: { map: { value: spriteTexture } },
				vertexShader: starVertexShader,
				fragmentShader: glowFragmentShader,
				vertexColors: true,
				transparent: true,
				depthWrite: false,
				blending: THREE.AdditiveBlending,
			}),
		[spriteTexture],
	);

	return (
		<group>
			{/* Glow layer (behind) */}
			<points ref={glowRef} renderOrder={0} material={glowMaterial}>
				<bufferGeometry>
					<bufferAttribute attach="attributes-position" args={[positions, 3]} />
					<bufferAttribute attach="attributes-color" args={[colors, 3]} />
					<bufferAttribute attach="attributes-size" args={[glowSizes, 1]} />
					<bufferAttribute attach="attributes-opacity" args={[opacities, 1]} />
				</bufferGeometry>
			</points>

			{/* Core dots (front) */}
			<points ref={coreRef} renderOrder={1} material={coreMaterial}>
				<bufferGeometry>
					<bufferAttribute attach="attributes-position" args={[positions, 3]} />
					<bufferAttribute attach="attributes-color" args={[colors, 3]} />
					<bufferAttribute attach="attributes-size" args={[coreSizes, 1]} />
					<bufferAttribute attach="attributes-opacity" args={[opacities, 1]} />
				</bufferGeometry>
			</points>
		</group>
	);
}

function GalaxyDistanceRings(): React.JSX.Element {
	const ringData = useMemo(() => {
		return DISTANCE_RINGS.map((radius) => {
			const r = radius * BASE_SCALE;
			const segments = 128;
			const pts: [number, number, number][] = [];
			for (let i = 0; i <= segments; i++) {
				const angle = (i / segments) * Math.PI * 2;
				pts.push([Math.cos(angle) * r, Math.sin(angle) * r, 0]);
			}
			return { radius, pts };
		});
	}, []);

	return (
		<group renderOrder={-1}>
			{ringData.map(({ radius, pts }) => (
				<group key={radius}>
					<Line
						points={pts}
						color="#ffffff"
						transparent
						opacity={0.03}
						lineWidth={1}
						dashed
						dashSize={8}
						gapSize={8}
					/>
					<Html
						position={[radius * BASE_SCALE + 4, 4, 0]}
						zIndexRange={[15, 0]}
						style={{
							color: "rgba(255,255,255,0.19)",
							fontSize: "10px",
							fontFamily: "Inter",
							pointerEvents: "none",
							whiteSpace: "nowrap",
						}}
					>
						{radius} ly
					</Html>
				</group>
			))}
		</group>
	);
}

function GalaxySolMarker(): React.JSX.Element {
	const ringRef = useRef<THREE.Mesh>(null);
	const glowTexture = useMemo(() => getStarGlowTexture("G"), []);

	useFrame(({ clock }) => {
		if (!ringRef.current) return;
		const t = clock.getElapsedTime();
		const pulse = 1 + 0.1 * Math.sin(t * 2);
		ringRef.current.scale.setScalar(pulse);
		(ringRef.current.material as THREE.MeshBasicMaterial).opacity = 0.3 + 0.12 * Math.sin(t * 2);
	});

	return (
		<group>
			{/* Warm glow behind Sol */}
			<sprite position={[0, 0, -0.1]} scale={[24, 24, 1]}>
				<spriteMaterial
					map={glowTexture}
					transparent
					opacity={0.5}
					depthWrite={false}
					blending={THREE.AdditiveBlending}
				/>
			</sprite>
			{/* Pulsing ring indicator */}
			<mesh ref={ringRef} position={[0, 0, 0.1]}>
				<ringGeometry args={[5, 5.6, 48]} />
				<meshBasicMaterial color="#fff4ea" transparent opacity={0.35} side={THREE.DoubleSide} />
			</mesh>
			{/* Label */}
			<Html
				position={[0, -12, 0]}
				center
				zIndexRange={[15, 0]}
				style={{
					color: "rgba(244, 234, 222, 0.75)",
					fontSize: "10px",
					fontFamily: "Inter",
					fontWeight: "500",
					letterSpacing: "0.05em",
					pointerEvents: "none",
					textShadow: "0 0 6px rgba(229, 160, 64, 0.4)",
				}}
			>
				Sol
			</Html>
		</group>
	);
}

function GalaxyDensityFog({ clusters }: { clusters: DensityCluster[] }): React.JSX.Element {
	const fogTexture = useMemo(() => getDensityFogTexture(), []);

	return (
		<group renderOrder={-2}>
			{clusters.map((cluster) => (
				<sprite
					key={`fog-${cluster.x.toFixed(0)}-${cluster.y.toFixed(0)}`}
					position={[cluster.x, -cluster.y, -1]}
					scale={[cluster.radius * 2, cluster.radius * 2, 1]}
				>
					<spriteMaterial
						map={fogTexture}
						transparent
						opacity={cluster.density * 0.3}
						depthWrite={false}
					/>
				</sprite>
			))}
		</group>
	);
}

function GalaxyHoverHighlight(): React.JSX.Element | null {
	// Disabled - visual feedback now comes from tooltip only
	return null;
}

// --- Main GalaxyView Component ---

export function GalaxyView(): React.JSX.Element {
	const systems = useObserverStore((s) => s.systems);
	const enterSystemView = useObserverStore((s) => s.enterSystemView);
	const tooltipRef = useRef<HTMLDivElement | null>(null);
	const hoveredRef = useRef<PositionedSystem | null>(null);
	const { gl } = useThree();
	const { isMobile } = useDeviceCapabilities();
	const lastUpdateRef = useRef(0);

	// Camera sync
	useViewportSync();

	// Find the tooltip element rendered by SpaceCanvas (outside Canvas / DOM reconciler)
	useEffect(() => {
		const container = gl.domElement.closest(".space-canvas-container");
		tooltipRef.current = container?.querySelector(".galaxy-tooltip") as HTMLDivElement | null;
	}, [gl.domElement]);

	// Density clusters
	const densityClusters = useMemo(() => computeDensityClusters(systems), [systems]);

	// Hover handler
	const handleHover = useCallback(
		(sys: PositionedSystem | null, screenX: number, screenY: number) => {
			hoveredRef.current = sys;
			const tip = tooltipRef.current;
			if (!tip) return;

			if (sys) {
				const star = sys.system.star;
				const color = getStarColor(star.spectralClass);
				const planetCount = sys.system.planets.length;

				tip.textContent = "";

				const dot = document.createElement("span");
				dot.className = "tooltip-star-dot";
				dot.style.background = color;

				const name = document.createElement("strong");
				name.textContent = star.name;

				const meta = document.createElement("span");
				meta.className = "tooltip-meta";
				meta.textContent = `${star.spectralClass} \u00b7 ${star.distanceLightYears.toFixed(1)} ly \u00b7 ${planetCount} planet${planetCount !== 1 ? "s" : ""}`;

				const zoneMeta = document.createElement("span");
				zoneMeta.className = "tooltip-meta tooltip-zone";
				zoneMeta.textContent = `Zone ${sys.system.zone} \u00b7 ${formatZoneDuration(sys.system.zone)} \u00b7 ${formatZoneMultiplier(sys.system.zone)}`;

				tip.appendChild(dot);
				tip.appendChild(name);
				tip.appendChild(meta);
				tip.appendChild(zoneMeta);
				tip.style.borderLeftColor = color;

				// Mobile: fixed position at top-center (near screen center for easy tapping)
				// Desktop: follow cursor
				if (isMobile) {
					tip.style.left = "50%";
					tip.style.top = "40%";
					tip.style.bottom = "auto";
					tip.style.transform = "translate(-50%, -50%)";
					tip.style.pointerEvents = "auto";
					tip.style.cursor = "pointer";
				} else {
					tip.style.left = `${screenX + 16}px`;
					tip.style.top = `${screenY - 12}px`;
					tip.style.bottom = "auto";
					tip.style.transform = "none";
					tip.style.pointerEvents = "none";
					gl.domElement.style.cursor = "pointer";
				}
				tip.style.display = "flex";
			} else {
				tip.style.display = "none";
				gl.domElement.style.cursor = "grab";
			}
		},
		[gl.domElement, isMobile],
	);

	// Mobile: center-tracking (throttled to 100ms)
	useFrame(({ clock }) => {
		if (!isMobile) return;

		const now = clock.getElapsedTime() * 1000; // Convert to ms
		if (now - lastUpdateRef.current < 100) return; // Throttle to 100ms
		lastUpdateRef.current = now;

		const canvas = gl.domElement;
		const rect = canvas.getBoundingClientRect();
		const centerScreenX = rect.width / 2;
		const centerScreenY = rect.height / 2;

		const { worldX, worldY } = screenToWorld(centerScreenX, centerScreenY);

		// Use 2x touch radius as max distance threshold
		const maxDistance = (HIT_RADIUS_TOUCH * 2) / useObserverStore.getState().galaxyView.scale;
		let nearest: PositionedSystem | null = null;
		let minDist = maxDistance;

		for (const system of systems) {
			const px = system.x * BASE_SCALE;
			const py = system.y * BASE_SCALE;
			const dist = Math.hypot(px - worldX, py - worldY);
			if (dist < minDist) {
				minDist = dist;
				nearest = { system, px, py };
			}
		}

		handleHover(nearest, centerScreenX, centerScreenY);
	});

	// Click handler — uses larger hit radius for touch
	useEffect(() => {
		const canvas = gl.domElement;
		const handler = (e: Event) => {
			const detail = (e as CustomEvent).detail;
			const { worldX, worldY } = screenToWorld(detail.screenX, detail.screenY);

			const isTouch = detail.pointerType === "touch" || detail.pointerType === "pen";
			const hitRadius = isTouch ? HIT_RADIUS_TOUCH : HIT_RADIUS_MOUSE;
			const threshold = hitRadius / useObserverStore.getState().galaxyView.scale;
			let nearest: StarSystem | null = null;
			let minDist = threshold;

			for (const system of systems) {
				const px = system.x * BASE_SCALE;
				const py = system.y * BASE_SCALE;
				const dist = Math.hypot(px - worldX, py - worldY);
				if (dist < minDist) {
					minDist = dist;
					nearest = system;
				}
			}

			if (nearest) {
				// Hide tooltip on touch tap (selecting opens sidebar instead)
				if (isTouch && tooltipRef.current) {
					tooltipRef.current.style.display = "none";
				}
				enterSystemView(nearest);
			}
		};

		canvas.addEventListener("canvas-click", handler);
		return () => canvas.removeEventListener("canvas-click", handler);
	}, [systems, enterSystemView, gl.domElement]);

	// Mobile: tooltip click handler to enter system view
	useEffect(() => {
		if (!isMobile) return;

		const tip = tooltipRef.current;
		if (!tip) return;

		const handleTooltipClick = (e: MouseEvent) => {
			e.stopPropagation();
			const system = hoveredRef.current?.system;
			if (system) {
				enterSystemView(system);
			}
		};

		tip.addEventListener("click", handleTooltipClick);
		return () => tip.removeEventListener("click", handleTooltipClick);
	}, [isMobile, enterSystemView]);

	return (
		<group>
			<NebulaBackground />
			<ParallaxStars />
			<AmbientColorPatches />
			<GalaxyDensityFog clusters={densityClusters} />
			<GalaxyDistanceRings />
			<ConstellationLines systems={systems} />
			<GalaxySolMarker />
			<GalaxyStars systems={systems} onHover={handleHover} />
			<GalaxyHoverHighlight />
		</group>
	);
}
